import { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { API_BASE_URL } from '../constants';
import { getCookie } from './cookie';

type ApiArgs = string | FetchArgs;

const SENSITIVE_KEYS = ['authorization', 'token', 'password', 'initdata', 'photo', 'file'];
const MAX_STRING_LENGTH = 2000;

export const logApiError = (
  args: ApiArgs,
  error: FetchBaseQueryError,
  meta?: FetchBaseQueryMeta
) => {
  const url = getRequestUrl(args);

  if (url.includes('/client-errors')) {
    return;
  }

  const payload = {
    request: {
      method: getRequestMethod(args),
      url,
      body: serializeBody(typeof args === 'string' ? undefined : args.body),
    },
    response: {
      status: error.status,
      data: redact(error.data),
      request_id: meta?.response?.headers.get('x-request-id') ?? undefined,
    },
    page: typeof window !== 'undefined' ? window.location.href : undefined,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    occurred_at: new Date().toISOString(),
  };

  void sendClientError(payload);
};

const sendClientError = async (payload: unknown) => {
  try {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
    const token = getCookie('token');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    await fetch(`${API_BASE_URL.replace(/\/$/, '')}/client-errors`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    // Не мешаем пользователю основным действием, если логирование само не прошло.
  }
};

const getRequestUrl = (args: ApiArgs): string => (
  typeof args === 'string' ? args : args.url
);

const getRequestMethod = (args: ApiArgs): string => (
  typeof args === 'string' ? 'GET' : args.method ?? 'GET'
);

const serializeBody = (body: unknown): unknown => {
  if (!body) {
    return undefined;
  }

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const result: Record<string, unknown> = {};

    body.forEach((value, key) => {
      result[key] = value instanceof File ? '[file]' : redact(value);
    });

    return result;
  }

  if (typeof body === 'string') {
    try {
      return redact(JSON.parse(body));
    } catch {
      return truncate(body);
    }
  }

  return redact(body);
};

const redact = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(redact);
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};

    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      if (SENSITIVE_KEYS.includes(key.toLowerCase())) {
        result[key] = '[redacted]';
        return;
      }

      result[key] = redact(item);
    });

    return result;
  }

  if (typeof value === 'string') {
    return truncate(value);
  }

  return value;
};

const truncate = (value: string): string => (
  value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}... [truncated]`
    : value
);
